/**
 * Mixpanel action utilities
 *
 * Wrapper functions around mixpanel-browser to provide consistent
 * interface and handle fallbacks for unavailable values.
 */

import mixpanel from "mixpanel-browser";
import { accountApi } from "@/shared/services";
import { BACKEND_EVENTS } from "../constants";
import { useAnalyticsStore } from "../store";
import { handleFallback } from "./handle-fallback.utils";
import { queueEvents } from "./handle-track-queue.utils";

const identify = (userId: string) => {
  mixpanel.identify(userId);
};

const alias = (anonymousId: string, userId: string) => {
  mixpanel.alias(userId, anonymousId);
};

const reset = () => {
  mixpanel.reset();
};

const register = (properties: Record<string, unknown>) => {
  mixpanel.register(handleFallback(properties));
};

const unregister = (keys: string[]) => {
  for (const key of keys) {
    mixpanel.unregister(key);
  }
};

const get_property = (key: string) => {
  return mixpanel.get_property(key);
};

const setPeople = (properties: Record<string, unknown>) => {
  mixpanel.people.set(handleFallback(properties));
};

const incrementor = (key: string) => {
  return (get_property(key) || 0) + 1;
};

/**
 * Get all mixpanel properties from cookie
 */
const getAllMixpanelProperties = (): Record<string, unknown> => {
  // mixpanel.cookie.props contains all super properties (registered properties)
  // This is a runtime property not included in TypeScript definitions
  const mixpanelWithCookie = mixpanel as typeof mixpanel & {
    cookie?: { props?: Record<string, unknown> };
  };
  return mixpanelWithCookie.cookie?.props || {};
};

/**
 * Track an event with Mixpanel
 */
const track = (
  event: string,
  properties: Record<string, unknown> = {},
): void => {
  if (useAnalyticsStore.getState().blocked) return;

  if (!useAnalyticsStore.getState().initialized) {
    queueEvents.handleSetTrackQueue({ event, properties });
    return;
  }

  if (BACKEND_EVENTS.has(event)) {
    accountApi.trackEvent(event, {
      ...getAllMixpanelProperties(),
      ...properties,
    });
    return;
  }

  mixpanel.track(event, properties);
};

const startSessionRecording = () => {
  mixpanel.start_session_recording();
};

export {
  identify,
  alias,
  reset,
  register,
  unregister,
  get_property,
  setPeople,
  incrementor,
  track,
  startSessionRecording,
};
